import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Grid,
  Alert,
  useTheme
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  WifiOff as WifiOffIcon
} from '@mui/icons-material';
import { useUserStore } from '@stores/userStore';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { useTranslation } from 'react-i18next';
import { McpSetupCard } from './McpSetupCard';

export const UserProfile = () => {
  const theme = useTheme();
  const { userName, fullName, email } = useUserStore();
  const isOnline = useOnlineStatus();
  const { t } = useTranslation();

  if (!isOnline) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          textAlign: 'center',
          p: 4
        }}
      >
        <WifiOffIcon
          sx={{
            fontSize: 80,
            color: 'text.disabled',
            opacity: 0.5,
            mb: 2
          }}
        />
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
          {t('settings:offline.title.userProfile')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 500 }}
        >
          {t('settings:offline.message.userProfile')}
        </Typography>
      </Box>
    );
  }

  const displayName = fullName || userName || 'User';
  const displayEmail = email || '';

  // Split name for first/last name display
  const nameParts = displayName.split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

  // Generate initials for avatar
  const initials = displayName
    .split(' ')
    .map(name => name.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          {t('settings:titles.userProfile')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('settings:descriptions.userProfile')}
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  fontSize: '2rem',
                  fontWeight: 600,
                  bgcolor: 'primary.main'
                }}
              >
                {initials}
              </Avatar>
              <IconButton
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  bgcolor: 'background.paper',
                  border: `2px solid ${theme.palette.divider}`,
                  width: 32,
                  height: 32,
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
                size="small"
              >
                <CameraIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            <Box sx={{ ml: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                {displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {displayEmail}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('settings:profile.firstName')}
                value={firstName}
                variant="outlined"
                InputProps={{
                  readOnly: true
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'action.hover'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('settings:profile.lastName')}
                value={lastName}
                variant="outlined"
                InputProps={{
                  readOnly: true
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'action.hover'
                  }
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <Button variant="contained" disabled sx={{ minWidth: 140 }}>
              {t('settings:profile.saveChanges')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <McpSetupCard />

      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {t('settings:profile.avatar.title')}
          </Typography>

          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('settings:profile.avatar.instructions')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('settings:profile.avatar.requirements')}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};
